import React, { useEffect, useState, type CSSProperties } from 'react';
import { getAllergenPdfDownloadName, getAllergenPdfUrlOptional } from '../api/allergenUtils';
import { Language } from '../api/Lang';

/**
 * React hook to get allergen PDF URL with loading state
 * @param lang - Language code ('el' or 'en')
 * @param targetDate - The target date (for popups: order date, for monthly: selected month)
 * @param isMonthlyMenu - Whether this is for monthly menu (use month) or popup (use start of week)
 * @returns Object with url (null when absent), loading
 */
export function useAllergenPdfUrl(lang: Language, targetDate?: Date, isMonthlyMenu: boolean = false) {
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        let isMounted = true;

        const fetchUrl = async () => {
            if (!targetDate) {
                if (isMounted) {
                    setLoading(false);
                    setUrl(null);
                }
                return;
            }

            setLoading(true);
            setUrl(null);

            const downloadUrl = await getAllergenPdfUrlOptional(lang, targetDate, isMonthlyMenu);
            if (isMounted) {
                setUrl(downloadUrl);
                setLoading(false);
            }
        };

        fetchUrl();

        return () => {
            isMounted = false;
        };
    }, [lang, targetDate, isMonthlyMenu]);

    return { url, loading };
}

/** Order app: allergens PDF download link — hidden while loading or when no file exists. */
export default function AllergenPdfDownloadLink({
    lang,
    targetDate,
    isMonthlyMenu = false,
    label,
    className = 'menu-title',
    style,
    linkStyle,
    imgStyle,
}: {
    lang: Language;
    targetDate?: Date;
    isMonthlyMenu?: boolean;
    label: string;
    className?: string;
    style?: CSSProperties;
    linkStyle?: CSSProperties;
    imgStyle?: CSSProperties;
}) {
    const { url, loading } = useAllergenPdfUrl(lang, targetDate, isMonthlyMenu);
    if (loading || !url) return null;

    return (
        <div className={className} style={{ cursor: 'pointer', ...style }}>
            <img src="/img-assets/pdf2.svg" style={{ marginRight: '4px', width: '25px', height: '25px', ...imgStyle }} alt="" />
            <a
                href={url}
                download={getAllergenPdfDownloadName(lang, targetDate, isMonthlyMenu)}
                style={{ fontSize: '14px', ...linkStyle }}
            >
                {label}
            </a>
        </div>
    );
}
